import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { BackButton } from "../../components/BackButton";
import { PhoneInput } from "../../components/PhoneInput";
import { claimEmail, requestOtp } from "../../api/auth";
import { ApiError } from "../../api/client";
import { DEFAULT_COUNTRY, normalisePhone, type CountryDialInfo } from "../../lib/phone";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fieldClass =
  "w-full bg-transparent text-[15px] text-text outline-none placeholder:text-text-muted";

/**
 * For students from the old app who signed up with a phone and no email.
 *
 * Sign-in is by email now, so they add one: the old phone number and password
 * prove the account is theirs, and a code sent to the new address proves the
 * email is theirs too.
 */
export function ClaimEmailScreen() {
  const navigate = useNavigate();
  const [country, setCountry] = useState<CountryDialInfo>(DEFAULT_COUNTRY);
  const [digits, setDigits] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const phone = normalisePhone(digits, country);
  const trimmedEmail = email.trim().toLowerCase();
  const detailsReady = phone !== null && password.length > 0 && EMAIL_PATTERN.test(trimmedEmail);

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await action();
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const sendCode = () =>
    run(async () => {
      await requestOtp(trimmedEmail, { purpose: "email_claim" });
      setCodeSent(true);
    });

  const confirm = () =>
    run(async () => {
      if (!phone) return;
      const session = await claimEmail({ phone, password, email: trimmedEmail, code });
      void navigate(session.student?.onboardingCompleted === false ? "/age" : "/home", {
        replace: true,
      });
    });

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/login" />

      <h1 className="m-0 pt-4 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.2]">
        Add an email to your account
      </h1>
      <p className="m-0 pt-2 text-[13.5px] leading-[1.55] text-text-muted">
        You joined with a phone number. Sign-in now uses email, so confirm your old details and the
        email you want to use.
      </p>

      <div className="flex flex-col gap-4 pt-6">
        <Field label="Phone number on your account" htmlFor="claim-phone">
          <PhoneInput
            id="claim-phone"
            value={digits}
            onChange={setDigits}
            country={country}
            onCountryChange={setCountry}
          />
        </Field>
        <Field label="Password" htmlFor="claim-password">
          <div className={`${inputShell} border-border-strong`}>
            <input
              id="claim-password"
              type="password"
              autoComplete="current-password"
              className={fieldClass}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
        </Field>
        <Field label="Email to add" htmlFor="claim-email">
          <div className={`${inputShell} border-border-strong`}>
            <input
              id="claim-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              className={fieldClass}
              placeholder="you@example.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setCodeSent(false);
              }}
            />
          </div>
        </Field>
        {codeSent ? (
          <Field label="Code we emailed you" htmlFor="claim-code">
            <div className={`${inputShell} border-border-strong`}>
              <input
                id="claim-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className={fieldClass}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              />
            </div>
          </Field>
        ) : null}
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-8">
        {failure ? (
          <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
            {failure}
          </p>
        ) : null}
        {codeSent ? (
          <Button onClick={() => void confirm()} disabled={code.length !== 6 || busy}>
            {busy ? "Checking…" : "Add email and sign in"}
          </Button>
        ) : (
          <Button onClick={() => void sendCode()} disabled={!detailsReady || busy}>
            {busy ? "Sending code…" : "Send code to this email"}
          </Button>
        )}
      </div>
    </div>
  );
}
